'use client';

import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { FiRefreshCw } from "react-icons/fi";
import NavBar from '@/components/header/NavBar';
import { useLocale } from 'next-intl';
import { GlowingEffect } from '@/components/home-page/glowing-effect';
import { Box, Lock, Search, Settings, Sparkles } from "lucide-react";
import Why from '@/components/home-page/whySection';
import ContactForm from '@/components/home-page/ContactForm';
import FAQContainer from '@/components/home-page/FAQContainer';
import Footer from '@/components/footer/Footer';


const ContactForms: React.FC = () => {

  return (<>
  {/* Fixed background — stays behind everything while page scrolls */}
  <div
    className="bg-cover bg-center bg-no-repeat h-96 lg:h-[500px] relative"
    style={{ backgroundImage: `url('/hero/health.avif')` }}
  >
   
<div className="absolute inset-0 bg-secondary/55 h-96 lg:h-[500px] " />
  <h1 className="absolute inset-0 flex items-center pt-20 lg:pt-40 justify-center text-white text-5xl lg:text-9xl font-extrabold text-center px-4 ">
   Health & Lifestyle
  </h1>
    
  </div>
 

  <NavBar />
   <ul className="grid grid-cols-1 bg-secondary grid-rows-none gap-4 md:grid-cols-12 md:grid-rows-3 lg:gap-4 p-8 xl:grid-rows-2">
      <GridItem
        area="md:[grid-area:1/1/2/7] xl:[grid-area:1/1/2/5]"
        icon={<Box className="h-4 w-4 text-black dark:text-primary" />}
        title="Healthcare Providers"
        description="We build secure, accessible websites and booking systems for clinics and practices, making it easy for patients to find care and schedule appointments."
      />
 
      <GridItem
        area="md:[grid-area:1/7/2/13] xl:[grid-area:2/1/3/5]"
        icon={<Settings className="h-4 w-4 text-black dark:text-primary" />}
        title="Fitness & Wellness"
        description="Get a digital presence that showcases your classes and services, simplifies bookings and memberships, and keeps clients engaged and coming back."
      />
 
      <GridItem
        area="md:[grid-area:2/1/3/7] xl:[grid-area:1/5/3/8]"
        icon={<Lock className="h-4 w-4 text-black dark:text-primary" />}
        title="Medical & Patient Data"
        description="We build secure, reliable digital solutions for health businesses, helping protect sensitive patient data and deliver seamless, compliant experiences for staff and patients."
      />
 
      <GridItem
        area="md:[grid-area:2/7/3/13] xl:[grid-area:1/8/2/13]"
        icon={<Sparkles className="h-4 w-4 text-black dark:text-primary" />}
        title="Beauty & Personal Care"
        description="We create high-performing digital solutions that help beauty and personal care businesses attract clients, showcase services, and turn online visitors into loyal customers."
      />
 
      <GridItem
        area="md:[grid-area:3/1/4/13] xl:[grid-area:2/8/3/13]"
        icon={<Search className="h-4 w-4 text-black dark:text-primary" />}
        title="Appointments & Scheduling"
        description="We build smart scheduling systems that simplify appointments, manage availability, and create seamless experiences for practitioners and clients."
      />
    </ul>
<Why/>
<ContactForm/>

<FAQContainer
  title={"Health &\nLifestyle FAQs"}
  description="Answers to the most common questions clients ask about our solutions for healthcare, fitness, and wellness businesses."
  faq={[
    {
      question: "Can you build a booking system for my clinic or practice?",
      answer:
        "Yes, we build secure appointment booking systems that make it easy for patients or clients to schedule and manage visits online.",
    },
    {
      question: "Do you build websites for gyms and fitness studios?",
      answer:
        "Yes, we create sites that showcase classes and services, simplify bookings and memberships, and help keep clients engaged.",
    },
    {
      question: "How do you handle sensitive patient or client data?",
      answer:
        "We build with security and privacy best practices from the start, so sensitive health and personal data stays protected.",
    },
    {
      question: "Can you build a site for a beauty or personal care business?",
      answer:
        "Absolutely. We build sites that showcase services, simplify bookings, and help attract and retain clients for beauty and personal care brands.",
    },
    {
      question: "Do you support recurring appointments or memberships?",
      answer:
        "Yes, we build scheduling systems that handle recurring bookings, memberships, and availability management for practitioners and studios.",
    },
    {
      question: "Do you offer support after the site or system launches?",
      answer:
        "Yes, we offer ongoing maintenance and support plans to keep your booking system or site running smoothly after launch.",
    },
  ]}
/>
<Footer/>
</>
  );
};

export default ContactForms;


interface GridItemProps {
  area: string;
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
}
 
const GridItem = ({ area, icon, title, description }: GridItemProps) => {
  return (
    <li className={`min-h-[14rem] list-none  rounded-3xl ${area}`}>
      <div className="relative h-full rounded-2xl border p-2 md:rounded-3xl md:p-3">
     <GlowingEffect

  glow={true}
  disabled={false}
  spread={40}
  proximity={64}
  inactiveZone={0.01}
  borderWidth={2}
/>
        <div className="border-0.75 relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-xl p-6 md:p-6 ">
          <div className="relative flex flex-1 flex-col justify-between gap-3">
            <div className="w-fit rounded-lg border text-primary border-white p-2">
              {icon}
            </div>
            <div className="space-y-3">
              <h3 className="-tracking-4 mt-8 text-xl  font-bold text-balance text-black md:text-3xl dark:text-primary">
                {title}
              </h3>
              <h2 className="text-sm/[1.125rem] text-black md:text-xl dark:text-white  font-light">
                {description}
              </h2>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};